import React, { useEffect, useMemo, useState } from 'react';
import DefaultPage from './DefaultPage';
import { getAllClients } from 'rest/makeSurveyClient';
import {
  ColumnDef,
  OnChangeFn,
  SortDirection,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import style from '@/styles/pages/Metrics.module.css';
import {
  IoIosArrowDropdownCircle,
  IoIosArrowDropupCircle,
} from 'react-icons/io';
import Loading from '@ax2/common/components/Loading';
import { history, getSearchParam } from '../helpers/router';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import _ from 'lodash';
import { ActionButton } from '@ax2/common/components/ActionButton';
import UserDataTableRow from 'components/UserDataTableRow';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from 'rest/authClient';

interface Data {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  companyRole: string;
  createdAt: string;
  isAdmin: boolean;
}

const UserManagement = () => {
  const [userData, setUserData] = useState<Data[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filtering, setFiltering] = useState<OnChangeFn<any>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>({});
  const updateError = useErrorUpdateContext();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const query = useMemo(() => new URLSearchParams(history.location.search), [
    history.location.search,
  ]);
  const initialPage = getSearchParam('page') || '1';
  const [pageNumber, setPageNumber] = useState<number>(~~initialPage);

  const navigateToPage = (paramKey: string, paramValue: string) => {
    query.set(paramKey, paramValue);

    query.forEach((value: string, key: string) => {
      if (value.trim() === '') {
        query.delete(key);
      }
    });

    history.push(`/user-management?${query.toString()}`);
  };

  const columns: ColumnDef<Data, any>[] = [
    { header: 'Client Email', accessorKey: 'email' },
    {
      header: 'First Name',
      accessorKey: 'firstName',
    },
    { header: 'Last Name', accessorKey: 'lastName' },
    { header: 'Company', accessorKey: 'companyName' },
    { header: 'Role', accessorKey: 'companyRole' },
    {
      header: 'Admin',
      accessorKey: 'isAdmin',
    },
  ];

  const table = useReactTable({
    data: userData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    autoResetExpanded: false,
  });

  const getAllClientsData = async () => {
    const currentPage = query.get('page') || `${pageNumber}`;
    return getAllClients({
      page: currentPage,
      email: query.get('email'),
      isAdmin:
        query.get('isAdmin') === 'true'
          ? true
          : query.get('isAdmin') === 'false'
          ? false
          : null,
    });
  };

  const onSearch = async () => {
    try {
      setLoading(true);
      const currentPage = query.get('page') || `${pageNumber}`;
      const result = await getAllClientsData();
      setPageNumber(~~currentPage);
      setUserData(result);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log('Failed to load Clients data: ', err);
      updateError({
        text: getErrorMessage(err as RestError),
      });
    }
  };

  const onAdminToggle = async () => {
    try {
      const result = await getAllClientsData();
      setUserData(result);
    } catch (err) {
      setLoading(false);
      console.log('Failed to load Clients data: ', err);
      updateError({
        text: getErrorMessage(err as RestError),
      });
    }
  };

  const handleSearch = () => {
    const _email = _.get(filters, 'email.value', '');
    const _isAdmin = _.get(filters, 'isAdmin.value', '');
    navigateToPage('page', `${pageNumber}`);
    navigateToPage('email', `${_email}`);
    navigateToPage('isAdmin', _isAdmin);
  };

  useEffect(() => {
    navigateToPage('page', `${pageNumber}`);
  }, [pageNumber]);

  useEffect(() => {
    let isCancel = false;
    if (!isCancel) {
      setFilters((prev: any) => ({
        ...prev,
        email: query.get('email')
          ? { value: query.get('email'), label: query.get('email') }
          : null,
        isAdmin: query.get('isAdmin')
          ? { value: query.get('isAdmin'), label: query.get('isAdmin') }
          : null,
      }));
      onSearch();
    }
    return () => {
      isCancel = true;
    };
  }, [query]);

  return (
    <DefaultPage
      header={{
        title: 'User Management',
        subtitle: 'User Profiles Management',
      }}
      wideContent={true}
      paddingTop={true}
    >
      <>
        <div className={style.filterContainer}>
          <div className={style.searchBlock}>
            <span>Email</span>
            <CreatableSelect
              isClearable
              value={filters.email}
              onChange={(e: any) =>
                setFilters((prev: any) => ({ ...prev, email: e }))
              }
            />
          </div>
          <div className={style.searchBlock}>
            <span>Admin</span>
            <Select
              value={filters.isAdmin}
              onChange={(e: any) =>
                setFilters((prev: any) => ({ ...prev, isAdmin: e }))
              }
              options={[
                { value: true, label: 'true' },
                { value: false, label: 'false' },
              ]}
              isClearable
            />
          </div>
          <div className={style.searchBlock}>
            <ActionButton text="Search" onClick={handleSearch} />
          </div>
        </div>

        {loading ? (
          <Loading paddingVertical={250} />
        ) : (
          <table className={`${style.table} ${style.userTable}`}>
            <thead className={style.thead}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className={style.tr}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className={style.th}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <div className={style.headings}>
                          <span className={style.headerTitle}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            &nbsp;
                            {
                              {
                                asc: <IoIosArrowDropdownCircle />,
                                desc: <IoIosArrowDropupCircle />,
                              }[header.column.getIsSorted() as SortDirection]
                            }
                          </span>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className={style.tr}>
                  {row.getVisibleCells().map(cell => (
                    <UserDataTableRow
                      key={cell.id}
                      row={row}
                      cell={cell}
                      onDataChange={onAdminToggle}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className={style.paginationButtons}>
          <span>Page {pageNumber}</span>
          <ActionButton
            onClick={() => {
              table.setPageIndex(0);
              setPageNumber(1);
            }}
            text="First page"
            size="small"
            disabled={pageNumber === 1}
          />

          <ActionButton
            onClick={() => {
              table.previousPage();
              setPageNumber(prev => prev - 1);
            }}
            text="Previous page"
            size="small"
            disabled={pageNumber === 1}
          />

          <ActionButton
            onClick={() => {
              table.nextPage();
              setPageNumber(prev => prev + 1);
            }}
            text="Next page"
            size="small"
            disabled={userData.length < 10}
          />
        </div>
      </>
    </DefaultPage>
  );
};

export default UserManagement;
